<template>
    <div>
        <div class="container-fluid">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <h4 class="m-0">
                    {{ trainingDetails.title }}
                </h4>
                <button class="btn btn-primary btn-sm no-print" @click="printReport">
                    <i class="fas fa-print"></i> Print Report
                </button>
            </div>
    
            <div class="row p-lg-3">
                <table class="table table-bordered compact-table">
                    <thead>
                        <tr>
                            <template v-if="trainingDetails && trainingDetails.district">
                                <th scope="col">Country</th>
                                <td>
                                    {{ (trainingDetails.district.province.country.title) || 'N/A' }}
                                </td>
                                <th scope="col">Province</th>
                                <td>
                                    {{ (trainingDetails.district.province.title) || 'N/A' }}
                                </td>
                                <th scope="col">City</th>
                                <td>
                                    {{ trainingDetails.district.title || 'N/A' }}
                                </td>
                            </template>
                            <template v-else>
                                <th scope="col">Country</th>
                                <td>No country added</td>
                                <th scope="col">Province</th>
                                <td>No province added</td>
                                <th scope="col">City</th>
                                <td>No City added</td>
                            </template>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th scope="row">Advisor</th>
                            <td>{{ trainingDetails.advisor.name || 'N/A' }}</td>
                            <th scope="row">Program Officer</th>
                            <td>{{ trainingDetails.programofficer.name || 'N/A' }}</td>
                            <th scope="row">Donor</th>
                            <td>
                                <template v-for="(doner) in doners">
                                    {{ doner.title }}
                                </template>
                            </td>
                        </tr>
                        <tr>
                            <th scope="row">Start Date</th>
                            <td>{{ formatDate4(trainingDetails.startdate)}}</td>
                            <th scope="row">End Date</th>
                            <td>{{ formatDate4(trainingDetails.enddate)}}</td>
                            <th scope="row">No. of Days</th>
                            <td>{{ trainingDetails.noofdays || 'N/A' }}</td>
                        </tr>
                        <tr>
                            <th scope="row">Sub Programme/Project</th>
                            <td colspan="5">{{ trainingDetails.subprogram.title || 'N/A' }}</td>
                        </tr>
                        <tr>
                            <th scope="row">Facilitators</th>
                            <td colspan="5">
                                <template v-for="(trainer) in alltrainers">
                                    {{ trainer.trainer.name }} ,</template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
    
            <!-- First Row of Charts -->
            <div class="row chart-row no-gutters ">
                <div class="col-md-3 col-gap">
                    <div class="card p-2 chart-container">
                        <h6 class="text-center chart-title">
                            Gender Segregation
                        </h6>
                        <div class="chart-wrapper">
                            <canvas id="genderChart" class="chart-size"></canvas>
                            <div class="color-legend">
                                <div v-for="(label, index) in ['Male', 'Female', 'Other']" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56'][index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-3 col-gap">
                    <div class="card p-2 chart-container">
                        <h6 class="text-center chart-title">
                            Average Actual knowledge change
                        </h6>
                        <div class="chart-wrapper" >
                            <canvas id="knowledge-change" class="knowledge-change chart-size"></canvas>
                            <div class="color-legend">
                                <div class="legend-item">
                                    <span class="color-box" style="background-color: #80ff80"></span>
                                    <span>Pre</span>
                                </div>
                                <div class="legend-item">
                                    <span class="color-box" style="background-color: #63b94b"></span>
                                    <span>Post</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Pre Questions 1 & 2 -->
                <div class="col-md-3 col-gap">
                    <div class="card p-2 chart-container">
                        <h6 class="text-center chart-title">
                            {{ preQuestions[0]?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper">
                            <canvas :id="'pre-chart-0'" class="chart-size"></canvas>
                            <div class="color-legend" v-if="preQuestions[0]">
                                <div v-for="(label, index) in preQuestions[0].label" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(preQuestions[0].label.length)[index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3 col-gap">
                    <div class="card p-2 chart-container">
                        <h6 class="text-center chart-title">
                            {{ preQuestions[1]?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper">
                            <canvas :id="'pre-chart-1'" class="chart-size"></canvas>
                            <div class="color-legend" v-if="preQuestions[1]">
                                <div v-for="(label, index) in preQuestions[1].label" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(preQuestions[1].label.length)[index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Second Row of Charts -->
            <div class="row chart-row no-gutters ">
                <!-- Left Column - Vertical Stacked Charts -->
                <div class="col-md-6 col-gap">
                    <div class="card p-2 mb-2 chart-container" style="height: 110px;">
                        <h6 class="text-center chart-title">
                            {{ preQuestions[2]?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper2">
                            <canvas :id="'pre-chart-2'" class="chart-size"></canvas>
                            <div class="color-legend" v-if="preQuestions[2]">
                                <div v-for="(label, index) in preQuestions[2].label" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(preQuestions[2].label.length)[index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="card p-2 mb-2 chart-container" style="height: 110px;">
                        <h6 class="text-center chart-title">
                            {{ postQuestions[0]?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper2">
                            <canvas :id="'post-chart-0'" class="chart-size"></canvas>
                            <div class="color-legend" v-if="postQuestions[0]">
                                <div v-for="(label, index) in postQuestions[0].label" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(postQuestions[0].label.length)[index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="card p-2 chart-container" style="height: 110px;">
                        <h6 class="text-center chart-title">
                            {{ postQuestions[1]?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper2">
                            <canvas :id="'post-chart-1'" class="chart-size"></canvas>
                            <div class="color-legend" v-if="postQuestions[1]">
                                <div v-for="(label, index) in postQuestions[1].label" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(postQuestions[1].label.length)[index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Right Column - Side by Side Charts -->
                <div class="col-md-3 col-gap">
                    <div class="card p-2 chart-container" style="height: 350px;">
                        <h6 class="text-center chart-title">
                            {{ postQuestions[2]?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper">
                            <canvas :id="'post-chart-2'" class="chart-size"></canvas>
                            <div class="color-legend" v-if="postQuestions[2]">
                                <div v-for="(label, index) in postQuestions[2].label" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(postQuestions[2].label.length)[index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-3 col-gap">
                    <div class="card p-2 chart-container" style="height: 350px;">
                        <h6 class="text-center chart-title">
                            {{ postQuestions[3]?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper">
                            <canvas :id="'post-chart-3'" class="chart-size"></canvas>
                            <div class="color-legend" v-if="postQuestions[3]">
                                <div v-for="(label, index) in postQuestions[3].label" :key="index" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(postQuestions[3].label.length)[index]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row chart-row no-gutters ">
                <div class="col-md-3 col-gap" v-for="(question, index) in postQuestions.slice(4)" :key="index + 4">
                    <div class="card p-2 chart-container">
                        <h6 class="text-center chart-title">
                            {{ question?.questionTitle || 'Loading...' }}
                        </h6>
                        <div class="chart-wrapper">
                            <canvas :id="'post-chart-' + (index + 4)" class="chart-size"></canvas>
                            <div class="color-legend" v-if="question">
                                <div v-for="(label, idx) in question.label" :key="idx" class="legend-item">
                                    <span class="color-box" :style="{backgroundColor: getBarColors(question.label.length)[idx]}"></span>
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import Chart from 'chart.js';
import {formatDate4} from "../../utils/DateFormate"

export default {
    data: () => ({
        trainingDetails: [],
        alltrainers: [],
        doners: [],
        trainingCode: "",
        maleCount: 0,
        femaleCount: 0,
        otherCount: 0,
        totalCount: 0,
        trainingID: "",
        preQuestions:[],
        postQuestions:[],
        basepath: process.env.VUE_APP_BASE_PATH,
        questions: [],
        actualKnowledgeChart: null,
        prePercent: 0,
        postPercent: 0,
        chartInstances: [] // To store chart instances for cleanup
    }),
    
    mounted() {
        axios.defaults.headers['Authorization'] = localStorage.getItem('token')
        this.trainingCode = this.$route.query.trainingId;
        this.openLoading();
        axios
            .get(
                process.env.VUE_APP_BASE_PATH +
                "/programOfficer/trainingsingle/get/" +
                this.trainingCode
            )
            .then(response => {
                if (response.data.unAuth) {
                    this.$gloabal.toastError('Your are Unauthorized. Need to Login Again');
                    localStorage.clear();
                    setTimeout(() => {
                        this.$router.push("/");
                    }, 1000);
                }
                if (response.data.tokenExpired) {
                    this.$gloabal.toastError(response.data.tokenExpired);
                    localStorage.clear();
                    setTimeout(() => {
                        this.$router.push("/");
                    }, 1000);
                }
                this.closeLoading();
                if (response.data && response.data.training) {
                    this.trainingDetails = response.data.training;
                    this.trainingID = response.data.training.id;
                    this.showTrainers(response.data.training.id);
                    this.showMaleFemale(response.data.training.id);
                    this.allStandardQuestions(response.data.training.id);
                    this.actualKnowledgechange(response.data.training.id);
                    this.allStandardPostQuestions(response.data.training.id);

                    if (response.data && response.data.doner) {
                        this.doners = response.data.doner;
                    }
                }
            });
    },

    beforeDestroy() {
        // Clean up all chart instances when component is destroyed
        this.chartInstances.forEach(chart => {
            if (chart) chart.destroy();
        });
    },

    methods: {
        formatDate4,
        printReport() {
            window.print();
         },
        
     showMaleFemale(tId) {
            axios
                .get(
                    process.env.VUE_APP_BASE_PATH +
                    "/single/training/traineedetails/report/gender/get/" +
                    tId
                )
                .then(response => {
                    this.closeLoading();
                    this.maleCount = response.data?.male;
                    this.femaleCount = response.data?.female;
                    this.otherCount = response.data?.other;
                    this.totalCount = response.data?.genderCount;
                    this.genderChart();
                });
        },   
        
     genderChart() {
             const ctx = document.getElementById("genderChart");
            if (ctx) {
                // Destroy existing chart if re-rendering
                const existingChart = this.chartInstances.find(c => c.canvas === ctx);
                if (existingChart) {
                    existingChart.destroy();
                }

                const data = [this.maleCount, this.femaleCount, this.otherCount];
                const total = this.totalCount || data.reduce((a, b) => a + b, 0);
                const labels = ["Male", "Female", "Other"];
                const colors = ["#36A2EB", "#FF6384", "#FFCE56"];

                const newChart = new Chart(ctx, {
                    type: "doughnut",
                    data: {
                        labels: labels,
                        datasets: [{
                            data: data,
                            backgroundColor: colors
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        legend: {
                            display: false
                        },
                        tooltips: {
                            callbacks: {
                                label: function (tooltipItem, chartData) {
                                    const value = chartData.datasets[0].data[tooltipItem.index];
                                    const percentage = ((value / total) * 100).toFixed(0);
                                    return `${chartData.labels[tooltipItem.index]}: ${percentage}% (${value})`;
                                }
                            }
                        },
                        plugins: {
            datalabels: {
                color: '#ffffff',
                anchor: 'center',
                align: 'center',
                formatter: function (value) {
                    return value + '%';
                },
                display: function(context) {
      return context.dataset.data[context.dataIndex] !== 0;
    },
                font: {
                    weight: 'bold',
                    size: 12
                }
            }
        },
                    },
                   
                });
                
                this.chartInstances.push(newChart);
            }
        },
        
        actualKnowledgechange(tId) {
            axios
                .get(
                    process.env.VUE_APP_BASE_PATH +
                    "/training/actual/knowledge-change/get/" +
                    tId
                )
                .then(async response => {
                    if (response.data.unAuth) {
                        this.$toasted.global.errorMsg('You are Unauthorized. Need to Login Again');
                        localStorage.clear();
                        setTimeout(() => {
                            this.$router.push("/");
                        }, 1000);
                        return;
                    }
                    if (response.data.tokenExpired) {
                        this.$toasted.global.errorMsg(response.data.tokenExpired);
                        localStorage.clear();
                        setTimeout(() => {
                            this.$router.push("/");
                        }, 1000);
                        return;
                    }

                    const pre = Math.round(response.data.prePercentage);
                    const post = Math.round(response.data.postPercentage);
                    this.prePercent = pre;
                    this.postPercent = post;

                    // Destroy old chart instance if it exists
                    if (this.actualKnowledgeChart) {
                        this.actualKnowledgeChart.destroy();
                    }

                    const ctx = document.getElementById("knowledge-change").getContext("2d");
                    this.actualKnowledgeChart = new Chart(ctx, {
                        type: 'bar',
                        data: {
                            labels: ['Pre', 'Post'],
                            datasets: [{
                                label: 'Knowledge Change',
                                data: [pre, post],
                                backgroundColor: ['#80ff80', '#63b94b']
                            }]
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            tooltips: {
                                callbacks: {
                                    label: function(tooltipItem, data) {
                                        return data.labels[tooltipItem.index] + ': ' + tooltipItem.yLabel + '%';
                                    }
                                }
                            },
                            scales: {
                                yAxes: [{
                                    ticks: {
                                        beginAtZero: true,
                                        max: 100,
                                        callback: function(value) {
                                            return value + '%';
                                        }
                                    },
                                    scaleLabel: {
                                        display: true,
                                        labelString: 'Percentage'
                                    }
                                }]
                            },
                            plugins: {
            datalabels: {
                color: '#ffffff',
                anchor: 'center',
                align: 'center',
                formatter: function (value) {
                    return value + '%';
                },
                display: function(context) {
      return context.dataset.data[context.dataIndex] !== 0;
    },
                font: {
                    weight: 'bold',
                    size: 12
                }
            }
        },
                            legend: {
                                display: false
                            }
                        }
                    });
                    
                    this.chartInstances.push(this.actualKnowledgeChart);
                });
            
        },

        allStandardQuestions(tId) {
            axios
                .get(
                    process.env.VUE_APP_BASE_PATH +
                    "/training/standardQuestionsNew/all/get/" +
                    tId
                )
                .then(response => {
                    this.preQuestions = response.data;
                    this.$nextTick(() => {
                        this.preQuestions.forEach((question, index) => {
                            this.renderPreChart(question, index);
                        });
                    });
                });
        },
        
  renderPreChart(chartData, index) {
            const ctx = document.getElementById(`pre-chart-${index}`);
            if (!ctx || !chartData) return;

            // Destroy existing chart if it exists
            const existingChart = this.chartInstances.find(c => c.canvas === ctx);
            if (existingChart) {
                existingChart.destroy();
            }

            const total = chartData.Options.reduce((sum, val) => sum + val, 0);
            const percentageData = chartData.Options.map(val => Math.round((val / total) * 100));
            
            if(index < 2) {
                const newChart = new Chart(ctx, {
    type: 'bar',
    data: {
        labels: [''],
        datasets: chartData.label.map((label, i) => ({
            label: `${label} (${percentageData[i]}%)`,
            data: [percentageData[i]],
            backgroundColor: this.getBarColors(chartData.label.length)[i],
        }))
    },
    options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: false
        },
        tooltips: {
            callbacks: {
                label: function (tooltipItem, data) {
                    const dataset = data.datasets[tooltipItem.datasetIndex];
                    return dataset.label;
                }
            }
        },
        plugins: {
            datalabels: {
                color: '#ffffff',
                anchor: 'center',
                align: 'center',
                formatter: function (value) {
                    return value + '%';
                },
                display: function(context) {
      return context.dataset.data[context.dataIndex] !== 0;
    },
                font: {
                    weight: 'bold',
                    size: 12
                }
            }
        },
        scales: {
            yAxes: [{
                stacked: true,
                ticks: {
                    beginAtZero: true,
                    max: 100,
                    callback: function (value) {
                        return value + '%';
                    }
                }
            }],
            xAxes: [{
                stacked: true,
                gridLines: { display: false }
            }]
        }
    },
    
});

                
                this.chartInstances.push(newChart);
            }
            
            if(index == 2) {
                const newChart = new Chart(ctx, {
                    type: 'horizontalBar',
                    data: {
                        labels: [''], // Single column
                        datasets: chartData.label.map((label, i) => ({
                            label: `${label} (${percentageData[i]}%)`,
                            data: [percentageData[i]], // Single data point per dataset
                            backgroundColor: this.getBarColors(chartData.label.length)[i],
                            borderWidth: 1
                        }))
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        legend: {
                            display: false
                        },
                        tooltips: {
                            callbacks: {
                                label: function(tooltipItem, data) {
                                    const dataset = data.datasets[tooltipItem.datasetIndex];
                                    return dataset.label;
                                }
                            }
                        },
                        plugins: {
            datalabels: {
                color: '#ffffff',
                anchor: 'center',
                align: 'center',
                formatter: function (value) {
                    return value + '%';
                },
                display: function(context) {
      return context.dataset.data[context.dataIndex] !== 0;
    },
                font: {
                    weight: 'bold',
                    size: 12
                }
            }
        },
                        scales: {
                            xAxes: [{
                                stacked: true,
                                ticks: {
                                    beginAtZero: true,
                                    max: 100,
                                    callback: function(value) {
                                        return value + '%';
                                    }
                                },
                                gridLines: {
                                    display: true
                                }
                            }],
                            yAxes: [{
                                stacked: true,
                                display: false // Hide y-axis labels since we only have one column
                            }]
                        },
                        animation: {
                            duration: 1000
                        }
                    }
                });
                this.chartInstances.push(newChart);
            }
        },

        allStandardPostQuestions(tId) {
            axios
                .get(
                    process.env.VUE_APP_BASE_PATH +
                    "/training/standardPostQuestionsNew/all/get/" +
                    tId
                )
                .then(response => {
                    this.postQuestions = response.data;
                    this.$nextTick(() => {
                        this.postQuestions.forEach((question, index) => {
                            this.renderPostChart(question, index);
                        });
                    });
                });
        },
        
        renderPostChart(chartData, index) {
            const ctx = document.getElementById(`post-chart-${index}`);
            if (!ctx || !chartData) return;

            // Destroy existing chart if it exists
            const existingChart = this.chartInstances.find(c => c.canvas === ctx);
            if (existingChart) {
                existingChart.destroy();
            }

            const total = chartData.Options.reduce((sum, val) => sum + val, 0);
            const percentageData = chartData.Options.map(val => Math.round((val / total) * 100));
           
            if(index < 2) {
                const newChart = new Chart(ctx, {
                    type: 'horizontalBar',
                    data: {
                        labels: [''], // Single column
                        datasets: chartData.label.map((label, i) => ({
                            label: `${label} (${percentageData[i]}%)`,
                            data: [percentageData[i]], // Single data point per dataset
                            backgroundColor: this.getBarColors(chartData.label.length)[i],
                            borderWidth: 1
                        }))
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        legend: {
                            display: false
                        },
                        tooltips: {
                            callbacks: {
                                label: function(tooltipItem, data) {
                                    const dataset = data.datasets[tooltipItem.datasetIndex];
                                    return dataset.label;
                                }
                            }
                        },
                        scales: {
                            xAxes: [{
                                stacked: true,
                                ticks: {
                                    beginAtZero: true,
                                    max: 100,
                                    callback: function(value) {
                                        return value + '%';
                                    }
                                },
                                gridLines: {
                                    display: true
                                }
                            }],
                            yAxes: [{
                                stacked: true,
                                display: false // Hide y-axis labels since we only have one column
                            }]
                        },
                        plugins: {
            datalabels: {
                color: '#ffffff',
                anchor: 'center',
                align: 'center',
                formatter: function (value) {
                    return value + '%';
                },
                display: function(context) {
      return context.dataset.data[context.dataIndex] !== 0;
    },
                font: {
                    weight: 'bold',
                    size: 12
                }
            }
        },
                        animation: {
                            duration: 1000
                        }
                    }
                });
                this.chartInstances.push(newChart);
            }
            
            if(index >= 2) {
                const newChart = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels: [''], // single bar
                        datasets: chartData.label.map((label, i) => ({
                            label: `${label} (${percentageData[i]}%)`,
                            data: [percentageData[i]],
                            backgroundColor: this.getBarColors(chartData.label.length)[i],
                        }))
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        legend: {
                            display: false
                        },
                        tooltips: {
                            callbacks: {
                                label: function (tooltipItem, data) {
                                    const dataset = data.datasets[tooltipItem.datasetIndex];
                                    return dataset.label;
                                }
                            }
                        },
                        scales: {
                            yAxes: [{
                                stacked: true,
                                ticks: {
                                    beginAtZero: true,
                                    max: 100,
                                    callback: function (value) {
                                        return value + '%';
                                    }
                                }
                            }],
                            xAxes: [{
                                stacked: true,
                                gridLines: { display: false }
                            }]
                        },
                        plugins: {
            datalabels: {
                color: '#ffffff',
                anchor: 'center',
                align: 'center',
                formatter: function (value) {
                    return value + '%';
                },
                display: function(context) {
      return context.dataset.data[context.dataIndex] !== 0;
    },
                font: {
                    weight: 'bold',
                    size: 12
                }
            }
        },
                    }
                });
                
                this.chartInstances.push(newChart);
            }
        },

        getBarColors(length) {
            const colorSets = {
                2: ['#548fd5', '#5f497a'],
                3: ['#548fd5', '#c0504e','#88a44f'],
                4: ['#548fd5', '#c0504e','#88a44f', '#705790'],
                5: ['#548fd5', '#c0504e','#88a44f', '#705790', '#4097ac'],
            };
            return colorSets[length] || ['#aaa']; // fallback
        },

        showTrainers(tId) {
            axios
                .get(
                    process.env.VUE_APP_BASE_PATH +
                    "/single/training/trainer/get/" +
                    tId
                )
                .then(response => {
                    this.closeLoading();
                    this.alltrainers = response.data;
                });
        },
        
        openLoading() {
            this.$vs.loading({
                type: "material",
            });
        },
        
        closeLoading() {
            this.$vs.loading.close();
        },
    }
};
</script>

<style scoped>
.compact-table {
    font-size: 14px;
    margin-bottom: 10px;
}

.compact-table th, .compact-table td {
    padding: 6px 10px;
}

.chart-row {
    margin-bottom: -5px;
}

.chart-container {
    height: 280px;
    /* margin-bottom: 10px; */
    width: 100%;
}

.chart-title {
    font-size: 13px;
    margin-bottom: 8px;
    font-weight: 500;
}

.chart-wrapper {
    position: relative;
    height: calc(100% - 70px);
    width: 100%;
}

.chart-wrapper2 {
    position: relative;
    height: calc(100% - 50px);
    width: 100%;
}

.color-legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 5px;
}

.legend-item {
    display: flex;
    align-items: center;
    margin: 0 5px;
    font-size: 11px;
}

.color-box {
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 5px;
    border: 1px solid #ddd;
}

.knowledge-change {
    height: 100% !important;
}

.color-box {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 5px;
  border: 1px solid #ddd;
  
}

@media print {
    body {
        padding: 20px;
        font-size: 12px;
    }
    
    .no-print {
        display: none !important;
    }
    
    .chart-container {
        page-break-inside: avoid;
    }
    .row {
    display: flex !important;
    flex-wrap: wrap !important;
  }

  .col-md-3, .col-md-6 {
    float: left !important;
    box-sizing: border-box;
  }

  .col-md-3 {
    width: 25% !important;
  }

  .col-md-6 {
    width: 50% !important;
  }

  .chart-container {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .chart-size{
    width: 100% !important;
  }
  .color-box {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

  }
}

</style>